import { z } from 'zod';
import { ConnectorKind } from './kind.js';
/**
 * Error codes thrown via `ConnectorError`. Centralized so the router's
 * status-code mapping + the adapter throw sites stay in sync. Adding
 * a new failure mode means appending to this enum (one place) instead
 * of hand-typing the string at each throw + match site.
 */
export const CONNECTOR_ERROR_CODES = [
    'vendor_not_configured',
    'invalid_state',
    'invalid_secrets',
    'oauth_exchange_failed',
    'profile_lookup_failed',
    'missing_installation_id',
    'missing_member',
    'use_manual_install',
    'no_secrets',
    'handle_event_failed',
    // OAuth token-refresh helper failures (apps/functions/src/connectors/token-refresh.ts).
    // Distinguished so the admin UI can surface "stale install — admin
    // must re-authorize" separately from a generic 5xx.
    'token_refresh_failed',
    // Vendor doesn't support refresh-token flow (GitHub installation tokens,
    // Stripe API keys). Throwing this means the caller used the wrong helper.
    'token_refresh_unsupported',
    // Dedup-store sentinel: the connector framework already processed
    // this event id. Not really an error — emitted by the dedup helper so
    // the orchestrator can `if (err.code === 'event_already_processed')`
    // skip downstream graph writes without a try/catch dance.
    'event_already_processed',
    // Watch-renewal failures (gmail users.watch / calendar push channels).
    'watch_renewal_failed',
];
export const ConnectorErrorCode = z.enum(CONNECTOR_ERROR_CODES);
/**
 * HTTP response error codes returned from `/connectors/*` endpoints.
 * Centralized so client/server stay in sync — adding a new failure mode
 * means appending to this enum (one place) instead of hand-typing the
 * string at each response site.
 */
export const CONNECTOR_RESPONSE_CODES = [
    'unauthorized',
    'no_matching_install',
    'no_secrets',
    'handle_failed',
    'not_installed',
    'cannot_install_for_other_member',
];
export const ConnectorResponseCode = z.enum(CONNECTOR_RESPONSE_CODES);
/**
 * Sentinel orgId used by adapters that can't bind an event to an org
 * during signature verification (Slack/Gmail/GitHub). The webhook
 * receiver replaces this with the real orgId after walking
 * `orgConnectorSecrets` for the matching vendor identifier.
 */
export const PENDING_ORG_ID = '__pending__';
/**
 * Per-connector audit action verbs. Combine with `connectorAuditAction(kind, verb)`
 * to produce the full action string (e.g. `connector.slack.event`). The
 * audit-store's `action` field is open-string-typed, so the enum is
 * for typo-safety + grep-ability at the call site, not a runtime gate.
 */
export const CONNECTOR_AUDIT_VERBS = [
    'install.start',
    'install-manual',
    'oauth.complete',
    'event',
    'backfill',
    'scope.update',
    'unsubscribe',
    // Cron-driven push-subscription renewal (Gmail users.watch, Calendar
    // push channels). Fires from `renewConnectorWatches` once per
    // expiring watch — distinct from `event` (inbound webhook) so the
    // audit feed can tell scheduled-maintenance writes apart from
    // user-driven writes.
    'watch.renew',
];
export const ConnectorAuditVerb = z.enum(CONNECTOR_AUDIT_VERBS);
/**
 * Build the canonical audit `action` string for a connector mutation.
 * Format: `connector.<kind>.<verb>` for per-kind verbs;
 * `connector.<verb>` for the kind-agnostic ones (install.start fires
 * before the OAuth dance completes; nothing is kind-prefixed).
 */
export function connectorAuditAction(kind, verb) {
    if (kind === null || verb === 'install.start') {
        return `connector.${verb}`;
    }
    return `connector.${kind}.${verb}`;
}
